import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { Usuario } from '../models/solicitation.model';
import { environment } from '../../environments/environment';

/**
 * Interface para dados do Microsoft Graph API
 */
interface GraphUser {
  userPrincipalName?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  mail?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  jobTitle?: string;
  department?: string;
  employeeId?: string;
}

interface GraphManager {
  userPrincipalName?: string;
  displayName?: string;
  mail?: string;
  businessPhones?: string[];
  jobTitle?: string;
  department?: string;
}

interface GraphUsersResponse {
  value?: GraphUser[];
}

export interface AzureUserLookup {
  samAccountName: string;
  displayName: string;
  email: string;
  title: string;
}

/**
 * Serviço de Autenticação
 * - Em desenvolvimento: busca dados do backend .NET que acessa o AD
 * - Em produção (futuro): usará Azure AD SSO
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<Usuario | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  /** Permissões de menu: definidas após a verificação ACF durante o carregamento inicial. */
  private readonly menuPermissionsSubject = new BehaviorSubject<{ canManageManagers: boolean; canManageParametros: boolean } | null>(null);
  readonly menuPermissions$ = this.menuPermissionsSubject.asObservable();

  private readonly backendUrl = environment.SERVER_URL + '/api';
  private loadingUser = false; // Flag para evitar múltiplas chamadas

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService
  ) {
    // Não carregar usuário no construtor - aguardar MSAL ficar pronto
  }

  /**
   * Carregar usuário atual
   * Usa Microsoft Graph API (via MSAL interceptor que adiciona token automaticamente)
   */
  public loadCurrentUser(): void {
    // Evitar múltiplas chamadas simultâneas
    if (this.loadingUser) {
      return;
    }
    
    // Verificar se está autenticado no MSAL
    if (this.msalService.instance.getAllAccounts().length === 0) {
      return; // Não autenticado ainda
    }

    this.loadingUser = true;
    this.loadUserFromGraph();
  }

  /**
   * Busca foto do usuário do Microsoft Graph
   * Endpoint: https://graph.microsoft.com/v1.0/me/photos/48x48/$value
   * @returns Blob URL da foto ou null se não disponível
   */
  getUserPhoto(): Observable<string | null> {
    return this.http.get('https://graph.microsoft.com/v1.0/me/photos/48x48/$value', {
      responseType: 'blob'
    }).pipe(
      map(blob => {
        // Converter blob para URL temporária que pode ser usada no <img>
        return URL.createObjectURL(blob);
      }),
      catchError(() => {
        // Usuário pode não ter foto configurada no Azure AD
        return of(null);
      })
    );
  }

  /**
   * Busca dados do usuário do backend .NET que acessa o Active Directory
   * Este método será removido quando migrar para Azure AD SSO
   */
  private loadUserFromBackend(): void {
    this.http.get<any>(`${this.backendUrl}/user/current`).pipe(
      map(response => {
        // Mapear resposta do backend para modelo Usuario
        const user: Usuario = {
          samAccountName: response.samAccountName,
          fullName: response.displayName,
          mail: response.email,
          telephoneNumber: response.telephoneNumber || '',
          title: response.title || '',
          department: response.department || '',
          centroCusto: response.department || '',
          manager: response.managerDisplayName || ''
        };
        
        // Se tem gerente, criar objeto gerente completo
        if (response.manager && response.managerDisplayName) {
          user.gerente = {
            samAccountName: response.manager,
            fullName: response.managerDisplayName,
            mail: '',  // Não temos do backend neste momento
            telephoneNumber: '',
            title: response.managerTitle || ''
          };
        }
        
        return user;
      }),
      catchError(error => {
        // ✅ ERRO REAL do backend/mainframe - já extraído pelo errorInterceptor
        const errorMsg = error.message || 'Erro desconhecido';
        alert(`ERRO: Não foi possível carregar dados do Active Directory.\n\n${errorMsg}\n\nVerifique:\n1. Backend rodando (${environment.SERVER_URL})\n2. Acesso ao Active Directory\n3. Console para detalhes`);
        
        return of(null);
      })
    ).subscribe(user => {
      if (user) {
        this.currentUserSubject.next(user);
      } else {
        this.currentUserSubject.next(null);
      }
    });
  }

  /**
   * Solicita informações do usuário via prompt e salva no localStorage
   * Usado quando o backend não está disponível
   */
  private loadDevelopmentUser(): void {
    // Verificar se já existe usuário salvo no localStorage
    const savedUser = localStorage.getItem('dev_user');
    
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        this.currentUserSubject.next(user);
        return;
      } catch {
        // MSAL não configurado ou erro silencioso - continuar sem login SSO
      }
    }

    // Solicitar informações do usuário
    const username = prompt('🔧 Modo de Desenvolvimento\n\nInforme seu username (ex: a999999):')?.trim();
    
    const fullName = prompt('🔧 Modo de Desenvolvimento\n\nInforme seu nome completo:')?.trim();
    
    if (!username || !fullName) {
      alert('Username e nome são obrigatórios!');
      this.loadDevelopmentUser(); // Tentar novamente
      return;
    }
    
    const devUser: Usuario = {
      samAccountName: username,
      fullName,
      mail: `${username}@volvo.com`,
      telephoneNumber: '',
      title: 'Developer',
      department: 'TI',
      centroCusto: 'TI-DEV'
    };
    
    // Salvar no localStorage
    localStorage.setItem('dev_user', JSON.stringify(devUser));
    
    this.currentUserSubject.next(devUser);
  }

  /**
   * Buscar dados completos do usuário do backend (Active Directory)
   */
  private loadUserFromGraph(): void {
    // Pegar conta ativa do MSAL para saber quem está logado
    const account = this.msalService.instance.getActiveAccount();
    
    if (!account) {
      this.currentUserSubject.next(null);
      return;
    }

    // Aguardar dados completos do AD antes de emitir o usuário.
    // O app NÃO deve sair da tela de loading sem todos os dados preenchidos.
    // O MsalInterceptor pode bloquear brevemente enquanto adquire token.
    // Timeout de 35s para cobrir cold starts do backend/mainframe.
    this.http.get<any>(`${this.backendUrl}/user/current`).pipe(
      timeout(35000)
    ).subscribe({
      next: (data) => {
        const usuario = this.buildUsuarioFromDataResponse(data, account);
        this.currentUserSubject.next(usuario);
        this.loadingUser = false;

        // Buscar foto em segundo plano e atualizar quando chegar
        this.getUserPhoto().subscribe(photoUrl => {
          if (photoUrl) {
            usuario.photoUrl = photoUrl;
            this.currentUserSubject.next({ ...usuario });
          }
        });
      },
      error: () => {
        // Backend indisponível ou timeout — não emite usuário.
        // O safetyTimeout do AppComponent tratará exibindo mensagem de erro.
        this.loadingUser = false;
      }
    });
  }

  /**
   * Mapeia dados do Graph/AD para modelo Usuario
   */
  private buildUsuarioFromDataResponse(data: any, account: { name?: string | null; username?: string }): Usuario {
    const usuario: Usuario = {
      samAccountName: data.samAccountName,
      fullName: data.displayName || account.name || '',
      mail: data.email || account.username || '',
      telephoneNumber: data.telephoneNumber || '',
      title: data.title || '',
      department: data.department || '',
      centroCusto: data.department || ''
    };
    this.enrichWithGerente(usuario, data);
    return usuario;
  }

  private enrichWithGerente(usuario: Usuario, data: any): void {
    if (data.manager && data.managerDisplayName) {
      usuario.gerente = {
        samAccountName: data.manager,
        fullName: data.managerDisplayName,
        mail: '',
        telephoneNumber: '',
        title: data.managerTitle || ''
      };
    }
  }

  /**
   * Mapeia dados do Graph/AD para modelo Usuario (endpoint Graph)
   */
  private mapGraphUserToUsuario(data: any): Usuario {
    const samAccountName = this.extractSamAccountName(data.userPrincipalName || data.mail || '');
    return {
      samAccountName,
      fullName: this.getFullName(data),
      mail: data.mail || '',
      telephoneNumber: this.getPhone(data),
      title: data.jobTitle || '',
      department: data.department || '',
      centroCusto: data.employeeId || ''
    };
  }

  private getFullName(data: any): string {
    return data.displayName || `${data.givenName || ''} ${data.surname || ''}`.trim();
  }

  private getPhone(data: any): string {
    return (data.businessPhones as string[] | undefined)?.[0] || data.mobilePhone || '';
  }

  /**
   * Extrai samAccountName do userPrincipalName
   * Ex: "a999999@volvo.com" -> "a999999"
   */
  private extractSamAccountName(upn: string): string {
    if (!upn) { return ''; }
    
    // Remover domínio se presente
    const parts = upn.split('@');
    let username = parts[0];
    
    // Remover domain\user se presente
    if (username.includes('\\')) {
      const domainParts = username.split('\\');
      username = domainParts.at(-1) || username;
    }
    
    return username.toLowerCase();
  }

  /**
   * 🚀 Buscar múltiplos usuários em BATCH (via backend .NET)
   * Mais performante que buscar um por um
   */
  getUsersBatch(samAccountNames: string[]): Observable<Usuario[]> {
    if (!samAccountNames || samAccountNames.length === 0) {
      return of([]);
    }

    return this.http.post<Usuario[]>(`${this.backendUrl}/user/batch`, samAccountNames).pipe(
      map(users => {
        return users;
      }),
      catchError(error => {
        return of([]); // Retorna array vazio em caso de erro
      })
    );
  }

  /**
   * Buscar múltiplos usuários no Azure AD (Microsoft Graph)
   * Tenta localizar por employeeId e por prefixo do UPN.
   */
  getUsersBatchFromAzure(samAccountNames: string[]): Observable<AzureUserLookup[]> {
    if (!samAccountNames || samAccountNames.length === 0) {
      return of([]);
    }

    const uniqueSamAccountNames = [...new Set(samAccountNames
      .map(name => name?.trim().toLowerCase() ?? '')
      .filter(name => !!name))];

    if (uniqueSamAccountNames.length === 0) {
      return of([]);
    }

    const requests = uniqueSamAccountNames.map(samAccountName =>
      this.searchUserInGraph(samAccountName).pipe(
        catchError(() => of(null))
      )
    );

    return forkJoin(requests).pipe(
      map(results => results.filter((item): item is AzureUserLookup => item !== null))
    );
  }

  private searchUserInGraph(samAccountName: string): Observable<AzureUserLookup | null> {
    const safeSam = samAccountName.replaceAll("'", "''");
    // Use mailNickname (alias/sAMAccountName) — mesmo campo usado pelo backend via Graph com Application permissions.
    // É o campo universal: presente em usuários com e sem sync on-premises.
    const filter = `mailNickname eq '${safeSam}'`;
    const endpoint = `https://graph.microsoft.com/v1.0/users?$select=displayName,mail,userPrincipalName,mailNickname,jobTitle&$top=1&$filter=${encodeURIComponent(filter)}`;
    console.info(`[searchUserInGraph] sam=${samAccountName} filter=${filter}`);

    return this.http.get<GraphUsersResponse>(endpoint).pipe(
      map(response => {
        const graphUser = response.value?.[0];
        if (!graphUser) {
          console.info(`[searchUserInGraph] Nenhum resultado para mailNickname='${samAccountName}'`);
          return null;
        }
        console.info(`[searchUserInGraph] Encontrado: ${samAccountName} -> ${graphUser.displayName} (UPN: ${graphUser.userPrincipalName})`);
        return {
          samAccountName: this.extractSamAccountName(graphUser.userPrincipalName || graphUser.mail || safeSam),
          displayName: graphUser.displayName || safeSam,
          email: graphUser.mail || graphUser.userPrincipalName || '',
          title: graphUser.jobTitle || ''
        };
      })
    );
  }

  /**
   * Obter usuário atual
   */
  getUser(): Observable<Usuario | null> {
    // Se ainda não carregou, carregar agora
    if (this.currentUserSubject.value === null && this.isLoggedIn()) {
      this.loadCurrentUser();
    }
    return this.currentUser$;
  }

  /**
   * Obter usuário atual de forma síncrona
   */
  getCurrentUserSync(): Usuario | null {
    return this.currentUserSubject.value;
  }

  /**
   * Obter samAccountName do usuário atual
   */
  getCurrentSamAccountName(): string {
    return this.currentUserSubject.value?.samAccountName || 'ANGULAR';
  }

  /**
   * Login via Azure AD
   */
  login(): void {
    this.msalService.loginRedirect();
  }

  /**
   * Logout
   */
  logout(): void {
    this.msalService.logout();
  }

  /**
   * Verificar se está logado
   */
  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Verifica autorização ACF no mainframe para o usuário atual.
   * Chamado durante o loading inicial, após a autenticação Azure AD.
   * O backend extrai o samAccountName diretamente do token JWT.
   * Retorna { authorized, message } — erros de rede/mainframe propagam via throwError.
   */
  checkAcfAuthorization(): Observable<{ authorized: boolean; message: string; canManageManagers?: boolean; canManageParametros?: boolean }> {
    return this.http.get<{ authorized: boolean; message: string; canManageManagers?: boolean; canManageParametros?: boolean }>(
      `${this.backendUrl}/authorization/acf-check`
    ).pipe(
      map(response => ({
        authorized: response.authorized,
        message: response.message ?? '',
        canManageManagers: response.canManageManagers ?? false,
        canManageParametros: response.canManageParametros ?? false
      }))
    );
  }

  /**
   * Atualiza as permissões de menu após a verificação ACF.
   * Chamado pelo AppComponent quando o resultado do acf-check chega.
   */
  setMenuPermissions(canManageManagers: boolean, canManageParametros: boolean): void {
    this.menuPermissionsSubject.next({ canManageManagers, canManageParametros });
  }

  /** Retorna as permissões de menu atuais (sincrono). */
  getMenuPermissions(): { canManageManagers: boolean; canManageParametros: boolean } {
    return this.menuPermissionsSubject.value ?? { canManageManagers: false, canManageParametros: false };
  }
}
